import { useMutation, useQuery } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Alert, Button, TextField } from '@kcm/ui'
import { ErrorCode, isKycSubmitted, ProfileSubmitRequest } from '@kcm/contracts'
import { useTranslate } from '@tolgee/react'
import { ApiError } from '../lib/api'
import { endpoints } from '../lib/endpoints'
import { asApiError, fieldError } from '../lib/validation'
import { AddressAutocomplete } from '../components/AddressAutocomplete'

/**
 * Post-KYC — confirm what Didit read (PLAN.md §11).
 *
 * The reason the name and address moved here from step 1 and step 2: Didit has
 * already read the legal name and, on most documents, the address. Confirming
 * beats typing — it is the lower-friction ask a lazy applicant actually
 * finishes, and it is filed from the same OCR the webhook would use anyway, so
 * the common path is a single tap.
 *
 * Everything stays editable: OCR is a starting point, not an assertion. The
 * address keeps its Places assist for the documents Didit could not read one
 * from (a passport carries none), and the fields the applicant confirms here
 * are the declared values that win over OCR when the CRM document is filed.
 */
export function ConfirmDetails() {
  const navigate = useNavigate()
  const { t } = useTranslate()
  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    dateOfBirth: '',
    address: '',
    city: '',
    state: '',
  })
  // Seeds from OCR exactly once, then leaves the fields to the user — a later
  // poll returning the same values must not wipe an edit in progress.
  const [prefilled, setPrefilled] = useState(false)

  const profile = useQuery({
    queryKey: ['kyc-profile'],
    queryFn: ({ signal }) => endpoints.profile.get(signal),
    staleTime: 30_000,
  })

  useEffect(() => {
    if (prefilled || !profile.data) return
    const p = profile.data.prefill
    setForm((f) => ({
      firstName: p.firstName ?? f.firstName,
      lastName: p.lastName ?? f.lastName,
      dateOfBirth: p.dateOfBirth ?? f.dateOfBirth,
      address: p.address ?? f.address,
      city: p.city ?? f.city,
      state: p.state ?? f.state,
    }))
    setPrefilled(true)
  }, [profile.data, prefilled])

  const submit = useMutation({
    mutationFn: async () => {
      const parsed = ProfileSubmitRequest.safeParse({
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        dateOfBirth: form.dateOfBirth,
        address: form.address.trim(),
        city: form.city.trim(),
        state: form.state.trim(),
      })
      // Parsed here against the same schema the API enforces, so a blank field
      // is caught before the round trip and shown on the field itself.
      if (!parsed.success) throw asApiError(parsed.error)
      return endpoints.profile.submit(parsed.data)
    },
    onSuccess: () => navigate('/compliance', { replace: true }),
  })

  const error = submit.error instanceof ApiError ? submit.error : null

  // Reached without a submitted identity — Didit was down, the session expired,
  // or the documents were declined. The form still stands: it is the applicant's
  // own name and address, which is worth collecting whether or not a document
  // ever backed it up, and it is the only route left to the rest of the funnel.
  // The fields simply arrive empty, because there was no OCR to prefill them.
  //
  // This is deliberately a notice and not a redirect to `KycOutcome`: that
  // screen's own continue button lands here, so bouncing back would loop.
  const state = profile.data?.state
  const identityOutstanding = Boolean(state && !isKycSubmitted(state))

  const set = (field: keyof typeof form) => (value: string) =>
    setForm((f) => ({ ...f, [field]: value }))

  return (
    <form
      className="flex flex-col gap-5"
      onSubmit={(e) => {
        e.preventDefault()
        if (!submit.isPending) submit.mutate()
      }}
    >
      <div>
        <h1 className="text-xl font-bold text-brand">{t('confirm.title')}</h1>
        <p className="mt-1 text-sm text-muted">{t('confirm.subtitle')}</p>
      </div>

      {identityOutstanding && (
        <Alert tone="info" title={t('confirm.idOutstandingTitle')}>
          {t('confirm.idOutstandingBody')}
        </Alert>
      )}

      {error && error.code !== ErrorCode.VALIDATION_FAILED && (
        <Alert tone="error" title={t('confirm.saveFailedTitle')}>
          {error.message}
        </Alert>
      )}

      <div className="grid gap-4 sm:grid-cols-2">
        <TextField
          required
          label={t('confirm.firstName')}
          name="given-name"
          autoComplete="given-name"
          value={form.firstName}
          onChange={(e) => set('firstName')(e.target.value)}
          disabled={submit.isPending}
          error={fieldError(error, 'firstName')}
        />
        <TextField
          required
          label={t('confirm.lastName')}
          name="family-name"
          autoComplete="family-name"
          value={form.lastName}
          onChange={(e) => set('lastName')(e.target.value)}
          disabled={submit.isPending}
          error={fieldError(error, 'lastName')}
        />
      </div>

      <TextField
        required
        type="date"
        label={t('confirm.dob')}
        name="bday"
        autoComplete="bday"
        value={form.dateOfBirth}
        onChange={(e) => set('dateOfBirth')(e.target.value)}
        disabled={submit.isPending}
        error={fieldError(error, 'dateOfBirth')}
      />

      <fieldset className="flex flex-col gap-4 rounded-xl border border-line bg-white p-5">
        <legend className="px-1 text-sm font-semibold text-ink">{t('confirm.whereLive')}</legend>
        <AddressAutocomplete
          value={form.address}
          onChange={(address) => setForm((f) => ({ ...f, address }))}
          // A pick fills all three address fields; each stays editable, because
          // the suggestion is Google's idea of the address and the bank
          // statement is the authority.
          onPick={(place) => setForm((f) => ({ ...f, ...place }))}
          disabled={submit.isPending}
          error={fieldError(error, 'address')}
        />
        <div className="grid gap-4 sm:grid-cols-2">
          <TextField
            required
            label={t('confirm.city')}
            name="address-level2"
            autoComplete="address-level2"
            value={form.city}
            onChange={(e) => set('city')(e.target.value)}
            disabled={submit.isPending}
            error={fieldError(error, 'city')}
          />
          <TextField
            required
            label={t('confirm.state')}
            name="address-level1"
            autoComplete="address-level1"
            value={form.state}
            onChange={(e) => set('state')(e.target.value)}
            disabled={submit.isPending}
            error={fieldError(error, 'state')}
          />
        </div>
      </fieldset>

      <Button type="submit" disabled={submit.isPending || profile.isPending}>
        {submit.isPending ? t('confirm.saving') : t('common.continue')}
      </Button>
    </form>
  )
}
